import { z } from 'zod';

// Debug Environment Response
// Returns masked environment variables organized by category
const DatabaseConnectionSchema = z.object({
  host: z.string(),
  port: z.string(),
  database: z.string(),
  connectionType: z.string(),
  ssl: z.string(),
  pooler: z.boolean(),
  transactionMode: z.boolean(),
});

export const DebugEnvResponseSchema = z.object({
  environment: z.object({
    nodeEnv: z.string().optional(),
    vercel: z.union([z.boolean(), z.string()]),
    vercelEnv: z.string().optional(),
    isProduction: z.boolean(),
    isDevelopment: z.boolean(),
  }),
  database: z.object({
    variables: z.record(z.string(), z.string()),
    activeConnection: DatabaseConnectionSchema.nullable(),
    activeUrl: z.string().nullable(),
  }),
  publicVariables: z.record(z.string(), z.string()),
  authVariables: z.record(z.string(), z.string()),
  otherNextVariables: z.record(z.string(), z.string()),
  counts: z.object({
    total: z.number(),
    public: z.number(),
    database: z.number(),
    auth: z.number(),
    other: z.number(),
  }),
});

// Debug Token Response
// Returns current session token information (masked)
export const DebugTokenResponseSchema = z.object({
  userId: z.string().nullable(),
  metadata: z
    .object({
      issuedAt: z.date().nullable(),
      expiresAt: z.date().nullable(),
      timeToExpiry: z.number().nullable(),
      isExpired: z.boolean().nullable(),
    })
    .nullable(),
  token: z.record(z.string(), z.unknown()).nullable(),
  twitterTokens: z
    .object({
      hasAccessToken: z.boolean(),
      hasRefreshToken: z.boolean(),
      expiresAt: z.date().nullable(),
      accessTokenPreview: z.string().nullable(),
      refreshTokenPreview: z.string().nullable(),
    })
    .nullable(),
  session: z.record(z.string(), z.unknown()).nullable(),
  timestamp: z.date(),
});

// Debug Session Response
// Returns decoded session data
export const DebugSessionResponseSchema = z.object({
  session: z.record(z.string(), z.unknown()),
  decoded: z.boolean(),
  timestamp: z.date(),
});

// Debug Cookies Response
// Returns all cookies from the request
export const DebugCookiesResponseSchema = z.object({
  cookies: z.record(z.string(), z.string()),
  count: z.number(),
});

export type DebugEnvResponse = z.infer<typeof DebugEnvResponseSchema>;
export type DebugTokenResponse = z.infer<typeof DebugTokenResponseSchema>;
export type DebugSessionResponse = z.infer<typeof DebugSessionResponseSchema>;
export type DebugCookiesResponse = z.infer<typeof DebugCookiesResponseSchema>;
